"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";
import { SHIFT_TYPES } from "../../../lib/shiftTypes";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";

function clock(value) {
  if (!value) return "Not set";
  return String(value).slice(0, 5);
}

function formatDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatStart(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function durationLabel(minutes) {
  const total = Number(minutes) || 0;
  if (!total) return "Not set";
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (!hours) return `${mins} minutes`;
  return `${hours} hour${hours === 1 ? "" : "s"}${mins ? ` ${mins} minutes` : ""}`;
}

export default function ShiftPage() {
  const { shiftId } = useParams();
  const { isEnabled } = useFeatureFlags();
  const roleRequestsEnabled = isEnabled("feature.shift_role_requests");
  const [user, setUser] = useState(null);
  const [shift, setShift] = useState(null);
  const [positions, setPositions] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestStatus, setRequestStatus] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);

  useEffect(() => {
    void loadPage();
  }, [shiftId]);

  async function getAccessToken() {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || "";
  }

  async function loadPage() {
    setLoading(true);
    setError("");
    try {
      const { data: authData } = await supabase.auth.getUser();
      const currentUser = authData?.user || null;
      if (!currentUser) throw new Error("Your session has expired. Please sign in again.");
      setUser(currentUser);

      const { data: shiftData, error: shiftError } = await supabase
        .from("shifts")
        .select("*")
        .eq("id", shiftId)
        .maybeSingle();
      if (shiftError) throw shiftError;
      if (!shiftData) throw new Error("This shift could not be found.");
      setShift(shiftData);

      const { data: allocationData, error: allocationError } = await supabase
        .from("shift_allocations")
        .select(`id, position_id, position:shift_positions!shift_allocations_position_id_fkey(id, position_name, section_name)`)
        .eq("shift_id", shiftId)
        .eq("user_id", currentUser.id);
      if (allocationError) throw allocationError;
      setAllocations(allocationData || []);

      const token = await getAccessToken();
      if (!token) throw new Error("Your session has expired. Please sign in again.");
      const response = await fetch(`/api/shift-allocation-request?shiftId=${encodeURIComponent(shiftId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));

      if (response.ok) {
        const allPositions = payload.positions || [];
        setPositions(allPositions);
        setSelectedRoleIds((allocationData || []).map((item) => item.position_id));
      } else if (payload.cancelled) {
        setPositions([]);
      } else {
        throw new Error(payload.error || "Unable to load shift positions.");
      }
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  }

  async function saveRoleClaims() {
    if (!user?.id || !shift?.id || requestSubmitting) return;
    if (selectedRoleIds.length === 0 && allocations.length > 0) {
      const confirmed = window.confirm("Release all of your claimed roles for this shift?");
      if (!confirmed) return;
    }
    setRequestSubmitting(true);
    setRequestStatus("Saving your roles…");
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Your session has expired. Please sign in again.");
      const response = await fetch("/api/shift-allocation-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shiftId: shift.id, positionIds: selectedRoleIds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Unable to save your shift roles.");
      setRequestStatus(payload.message || "Your roles have been claimed. No management approval is required.");
      await loadPage();
    } catch (err) {
      setRequestStatus(String(err.message || err));
    } finally {
      setRequestSubmitting(false);
    }
  }

  function toggleRole(positionId, checked) {
    setSelectedRoleIds((current) => checked
      ? [...new Set([...current, positionId])]
      : current.filter((id) => id !== positionId));
  }

  const details = shift?.shift_details || {};
  const type = shift ? (SHIFT_TYPES[shift.shift_type] || null) : null;
  const groupedPositions = useMemo(() => {
    const groups = new Map();
    for (const position of positions) {
      const section = position.section_name || "Staffing";
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section).push(position);
    }
    return Array.from(groups.entries());
  }, [positions]);

  const cancelled = shift?.shift_status === "Cancelled";
  const isPast = shift ? new Date(shift.time).getTime() < Date.now() : false;
  const claimedRoleIds = useMemo(() => new Set(allocations.map((item) => item.position_id)), [allocations]);
  const claimedRoles = useMemo(() => allocations.map((item) => item.position?.position_name).filter(Boolean), [allocations]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        <div className="nhsuk-width-container pguh-ecamis-shift-page">
          <div className="pguh-ecamis-shift-page__back">
            <Link to="/intranet/calendar" className="nhsuk-back-link">← Back to calendar</Link>
          </div>

          {loading ? (
            <div className="pguh-ecamis-shift-page__loading">Loading shift…</div>
          ) : error ? (
            <div className="nhsuk-error-summary" role="alert">
              <h1 className="nhsuk-error-summary__title">Unable to open shift</h1>
              <div className="nhsuk-error-summary__body"><p>{error}</p></div>
            </div>
          ) : shift ? (
            <>
              <header className={`pguh-ecamis-shift-hero${cancelled ? " is-cancelled" : ""}`}>
                <div>
                  <span className="pguh-public-kicker">eCAMIS · Shift</span>
                  <h1 className="nhsuk-heading-l">{shift.title}</h1>
                  <p className="nhsuk-body-l">{type?.label || "Shift"}</p>
                </div>
                <div className="pguh-ecamis-shift-hero__status">
                  <span className={`pguh-ecamis-shift-status ${cancelled ? "is-cancelled" : isPast ? "is-past" : "is-active"}`}>
                    {cancelled ? "Cancelled" : isPast ? "Started" : "Upcoming"}
                  </span>
                </div>
              </header>

              {cancelled && (
                <div className="nhsuk-warning-callout pguh-ecamis-shift-cancelled">
                  <h2 className="nhsuk-warning-callout__label">Shift cancelled</h2>
                  <p>{shift.cancellation_reason || "This shift will not take place."}</p>
                </div>
              )}

              <div className="pguh-ecamis-shift-layout">
                <section className="pguh-ecamis-shift-main">
                  <div className="pguh-ecamis-shift-panel">
                    <h2 className="nhsuk-heading-m">Shift details</h2>
                    <dl className="pguh-ecamis-shift-details">
                      <div><dt>Date</dt><dd>{formatDate(shift.time)}</dd></div>
                      <div><dt>Shift start</dt><dd>{formatStart(shift.time)}</dd></div>
                      <div><dt>Staff briefing</dt><dd>{clock(shift.briefing_time)}</dd></div>
                      <div><dt>Patient / public join</dt><dd>{clock(shift.admissions_time || details.public_join_time || details.shift_start_time)}</dd></div>
                      <div><dt>Duration</dt><dd>{durationLabel(shift.duration_minutes)}</dd></div>
                      <div><dt>Location</dt><dd>{shift.location || "TBC"}</dd></div>
                      {details.hospital && <div><dt>Hospital</dt><dd>{details.hospital}</dd></div>}
                      {details.ward && <div><dt>Ward</dt><dd>{details.ward}</dd></div>}
                      {details.day_night && <div><dt>Shift period</dt><dd>{details.day_night}</dd></div>}
                      <div><dt>Host</dt><dd>{shift.host || "TBC"}</dd></div>
                      {shift.shift_type === "emergency_department" && (
                        <div><dt>Major Injuries</dt><dd>{shift.major_injuries_allowed ? "Allowed" : "Not allowed"}</dd></div>
                      )}
                    </dl>
                  </div>

                  <div className="pguh-ecamis-shift-panel">
                    <div className="pguh-ecamis-shift-panel__heading">
                      <div>
                        <h2 className="nhsuk-heading-m">Allocation sheet</h2>
                        <p>Current staffing capacity for this shift.</p>
                      </div>
                    </div>
                    {groupedPositions.length === 0 ? (
                      <p>No staffing positions have been configured for this shift.</p>
                    ) : (
                      <div className="pguh-ecamis-staffing-groups">
                        {groupedPositions.map(([section, sectionPositions]) => (
                          <section key={section} className="pguh-ecamis-staffing-group">
                            <h3>{section}</h3>
                            <div className="pguh-ecamis-position-list">
                              {sectionPositions.map((position) => (
                                <div key={position.id} className={`pguh-ecamis-position-row${position.full ? " is-full" : ""}${claimedRoleIds.has(position.id) ? " is-mine" : ""}`}>
                                  <div>
                                    <strong>{position.position_name}</strong>
                                    <span>{claimedRoleIds.has(position.id) ? "Claimed by you" : position.full ? "Full" : `${position.remaining} space${position.remaining === 1 ? "" : "s"} available`}</span>
                                  </div>
                                  <span className="pguh-ecamis-position-capacity">{position.allocated_count}/{position.capacity}</span>
                                </div>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                <aside className="pguh-ecamis-shift-side">
                  <div className="pguh-ecamis-shift-panel pguh-ecamis-request-panel">
                    <h2 className="nhsuk-heading-m">Your shift roles</h2>

                    {!roleRequestsEnabled ? (
                      <div className="pguh-allocation-state is-cancelled"><strong>Role claiming disabled</strong><span>Shift role claiming is currently disabled by the developer.</span></div>
                    ) : cancelled ? (
                      <div className="pguh-allocation-state is-cancelled"><strong>Role changes closed</strong><span>This shift has been cancelled.</span></div>
                    ) : isPast ? (
                      <div className="pguh-allocation-state is-cancelled"><strong>Role changes closed</strong><span>This shift has already started.</span></div>
                    ) : positions.length === 0 ? (
                      <div className="pguh-allocation-state is-pending"><strong>No roles configured</strong><span>Management has not configured staffing roles for this shift yet.</span></div>
                    ) : (
                      <div className="pguh-ecamis-request-form pguh-multi-role-claim">
                        <p className="nhsuk-body-s"><strong>Claim every role you want.</strong> Your selection is applied immediately and does not need Management approval.</p>
                        {claimedRoles.length > 0 && <div className="pguh-allocation-state is-approved"><strong>Your current roles</strong><span>{claimedRoles.join(" · ")}</span></div>}
                        <div className="pguh-role-claim-list">
                          {positions.map((position) => {
                            const currentlyMine = claimedRoleIds.has(position.id);
                            const disabled = position.full && !currentlyMine;
                            const checked = selectedRoleIds.includes(position.id);
                            return <label key={position.id} className={`pguh-role-claim-option${checked ? " is-selected" : ""}${disabled ? " is-disabled" : ""}`}>
                              <input type="checkbox" checked={checked} disabled={disabled || requestSubmitting} onChange={(e) => toggleRole(position.id, e.target.checked)} />
                              <span><strong>{position.position_name}</strong><small>{currentlyMine ? "Currently claimed by you" : disabled ? "Full" : `${position.remaining} space${position.remaining === 1 ? "" : "s"} available`}</small></span>
                              <em>{position.allocated_count}/{position.capacity}</em>
                            </label>;
                          })}
                        </div>
                        <button type="button" className="nhsuk-button" disabled={requestSubmitting} onClick={saveRoleClaims}>
                          {requestSubmitting ? "Saving…" : selectedRoleIds.length ? `Save ${selectedRoleIds.length} claimed role${selectedRoleIds.length === 1 ? "" : "s"}` : "Release all roles"}
                        </button>
                      </div>
                    )}
                    {requestStatus && <div className="pguh-shift-save-status" role="status">{requestStatus}</div>}
                  </div>

                  <div className="pguh-ecamis-shift-panel pguh-ecamis-shift-links">
                    <h2 className="nhsuk-heading-s">Shift navigation</h2>
                    <Link to="/intranet/calendar">Calendar</Link>
                    <Link to="/intranet/myschedule">My shifts & allocations</Link>
                    <Link to="/intranet/ecamis/index">eCAMIS home</Link>
                  </div>
                </aside>
              </div>
            </>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}
